import { inject, Injectable } from '@angular/core';
import { Browser } from '@capacitor/browser';
import { Capacitor, PluginListenerHandle } from '@capacitor/core';
import { fromEventPattern, Observable } from 'rxjs';

import { AuthFailureError } from './auth-failure';
import { BrowserLocation } from './browser-location';

/**
 * The external user-agent a federated Grant happens in.
 *
 * `@capacitor/browser` and deliberately not a WebView: this is SFSafariViewController on iOS and a
 * Chrome Custom Tab on Android, which is what RFC 8252 §4 calls for. The difference is not
 * cosmetic — an in-app WebView would let the app read what is typed into Google's page, which is
 * both the thing an identity provider is there to prevent and the reason Google refuses to
 * authenticate in one.
 *
 * Amplify's own `signInWithRedirect` is not used for this leg. On the web build Capacitor runs,
 * `openAuthSession` is `window.location.href = url`, which navigates the WebView away from the app
 * and drops the resolved value — so the flow never completes, and the PKCE material would live in
 * `localStorage` under keys we do not control.
 *
 * ## On the web build there is no agent to open
 *
 * A browser has no Custom Tab to put this in, so the tab itself navigates and the response arrives
 * in a new page. That is why the Grant is persisted before {@link open} is called there — see
 * `rememberPendingAuthorization`. A popup would keep the JS context alive, but it would have to be
 * opened from an effect after `await createCodeChallenge`, outside the user-gesture task, which
 * Safari and Chrome block.
 */
@Injectable({ providedIn: 'root' })
export class ExternalAgent {
  private readonly location = inject(BrowserLocation);

  /**
   * Open the authorization request.
   *
   * A failure here is classified rather than left to escape, which is this adapter's whole reason
   * for existing as one. It is reachable — a device with no browser available to Custom Tabs cannot
   * do federated sign-in at all — and an unclassified error would leave the machine in
   * `preparingAuthorization` behind a spinner, with the password form it needs to fall back to
   * hidden underneath.
   */
  async open(url: string): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      // Navigating away ends this JS context, so nothing after this line runs — which is why the
      // Grant is persisted before this is called.
      this.location.assign(url);
      return;
    }

    try {
      await Browser.open({ url });
    } catch {
      throw new AuthFailureError(
        { kind: 'authorizationFailed', detail: 'agentUnavailable' },
        'the external user-agent could not be opened'
      );
    }
  }

  /**
   * Close the agent, if there is anything to close.
   *
   * Swallowing the failure is the point: a rejection here would put an alertable
   * `auth.command_failed` on a path that behaved exactly as designed.
   *
   * **Only ever issued once a callback has arrived**, or on a sign-out raised while a Grant is open.
   * `close()` is not a no-op on Android as of `@capacitor/browser` 8: `BrowserPlugin.close()` starts
   * `BrowserControllerActivity` with a `close` extra, which finishes the Custom Tab for real.
   * Combined with a `browserFinished` that Android *infers* rather than observes, issuing it on an
   * inferred dismissal would dismiss sign-ins that are still in progress — see the
   * `authorizationAbandoned` case in `transition()`, which issues no close for exactly that reason.
   */
  async close(): Promise<void> {
    // Nothing to close on web: the agent was this tab, and by the time a close is issued the tab is
    // already back on the app.
    if (!Capacitor.isNativePlatform()) return;

    try {
      await Browser.close();
    } catch {
      // Nothing open, or a platform that cannot close it. Neither is an outcome to report.
    }
  }

  /**
   * The person closed the agent without finishing.
   *
   * `fromEventPattern` rather than a manual subscription so the listener's lifetime is the
   * subscriber's. The second argument receives whatever the first returned, and `addListener`
   * returns a **promise** of a handle: calling `.remove()` on the promise is `undefined()`, a
   * `TypeError` raised during unsubscribe that RxJS wraps as `UnsubscriptionError`, leaving the
   * listener attached.
   *
   * This fires for a deliberate {@link close} too, which is why the effect that consumes it reads
   * the state before calling it an abandonment.
   */
  readonly dismissed$: Observable<void> = fromEventPattern<void>(
    handler => Browser.addListener('browserFinished', handler),
    (_handler, handle: Promise<PluginListenerHandle>) => void handle?.then(listener => listener.remove())
  );
}
